/**
 * OnboardingTooltip - Positioned card with directional arrow
 *
 * Renders a tooltip card with title, description, progress indicator,
 * and navigation controls. Positioned dynamically based on the target
 * element location.
 */

import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { TooltipPosition } from '@/lib/onboardingUtils';
import OnboardingProgressBar from './OnboardingProgressBar';
import OnboardingControls from './OnboardingControls';
import { cn } from '@/lib/utils';

interface OnboardingTooltipProps {
  /** i18n key for the title */
  titleKey: string;
  /** i18n key for the description */
  descriptionKey: string;
  /** Calculated position */
  position: TooltipPosition;
  /** Current chapter title key */
  chapterTitleKey: string;
  /** Current chapter index (0-based) */
  chapterIndex: number;
  /** Total number of chapters */
  totalChapters: number;
  /** Current global step index */
  globalStepIndex: number;
  /** Total steps across all chapters */
  totalSteps: number;
  /** Whether this is the first step overall */
  isFirstStep: boolean;
  /** Whether this is the last step overall */
  isLastStep: boolean;
  /** Whether this step requires clicking the target element */
  interactive?: boolean;
  /** Callback ref to report tooltip dimensions */
  onMeasure?: (width: number, height: number) => void;
  /** Navigation callbacks */
  onNext: () => void;
  onPrev: () => void;
  onDismiss: () => void;
}

const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  titleKey,
  descriptionKey,
  position,
  chapterTitleKey,
  chapterIndex,
  totalChapters,
  globalStepIndex,
  totalSteps,
  isFirstStep,
  isLastStep,
  interactive,
  onMeasure,
  onNext,
  onPrev,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(false);

  useEffect(() => {
    if (tooltipRef.current && onMeasure) {
      const { offsetWidth, offsetHeight } = tooltipRef.current;
      onMeasure(offsetWidth, offsetHeight);
      setMeasured(true);
    }
  }, [titleKey, descriptionKey, onMeasure]);

  return (
    <div
      ref={tooltipRef}
      className={cn(
        'onboarding-tooltip',
        !measured && 'onboarding-tooltip-measuring',
      )}
      style={{
        top: position.top,
        left: position.left,
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t(titleKey)}
    >
      {/* Arrow */}
      {position.arrowDirection !== 'none' && (
        <div
          className={cn(
            'onboarding-tooltip-arrow',
            `onboarding-tooltip-arrow-${position.arrowDirection}`
          )}
        />
      )}

      {/* Content */}
      <div className="p-3 md:p-4 space-y-2 md:space-y-3">
        {/* Chapter indicator */}
        <OnboardingProgressBar
          chapterTitleKey={chapterTitleKey}
          chapterIndex={chapterIndex}
          totalChapters={totalChapters}
          globalStepIndex={globalStepIndex}
          totalSteps={totalSteps}
        />

        {/* Title */}
        <h3 className="text-sm md:text-base font-semibold text-foreground leading-tight">
          {t(titleKey)}
        </h3>

        {/* Description */}
        <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
          {t(descriptionKey)}
        </p>

        {/* Controls */}
        <OnboardingControls
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          interactive={interactive}
          onNext={onNext}
          onPrev={onPrev}
          onDismiss={onDismiss}
        />
      </div>
    </div>
  );
};

export default OnboardingTooltip;
